import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

export const WebSocketContext = createContext();

// Singleton WebSocket instance - ensures only one connection exists
let wsInstance = null;
let activeSubscriptions = new Set();
let messageHandlers = {};
let reconnectTimeout = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_DELAY = 3000; // 3 seconds

// Default loading data for UI while fetching real data
const DEFAULT_LOADING_DATA = Array(3)
  .fill(null)
  .map((_, index) => ({
    id: index + 1,
    change_in_price: 0,
    price: 0,
    base: "Loading",
    quote: "....",
  }));

export const WebSocketProvider = ({ children }) => {
  const tokenRef = useRef(localStorage.getItem("token"));

  const ws = useRef(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [MKData, setMKData] = useState([]);
  const [volumeData, setVolumeData] = useState(DEFAULT_LOADING_DATA);
  const [increasingData, setIncreasingData] = useState(DEFAULT_LOADING_DATA);
  const [decreasingData, setDecreasingData] = useState(DEFAULT_LOADING_DATA);
  const [coinsData, setCoinsData] = useState([]);
  const [pairs, setPairs] = useState([]);

  // Trading page specific states
  const [asks, setAsks] = useState([]);
  const [bids, setBids] = useState([]);
  const [trades, setTrades] = useState([]);
  const [latestTradePrice, setLatestTradePrice] = useState(null);
  const [latestTradeType, setLatestTradeType] = useState(null);
  const [latestPrices, setLatestPrices] = useState(null);

  // User specific states (requires authentication)
  const [userBalance, setUserBalance] = useState({});
  const [userOpenOrders, setUserOpenOrders] = useState([]);

  // Register message handler for a specific event
  const registerHandler = useCallback((event, handler) => {
    messageHandlers[event] = handler;
  }, []);

  // Unregister message handler when component unmounts
  const unregisterHandler = useCallback((event) => {
    delete messageHandlers[event];
  }, []);

  // Central message handler - routes messages to appropriate handlers
  const handleMessage = useCallback((e) => {
    try {
      const data = JSON.parse(e.data);

      if (data.method === "pong") {
        return;
      }

      let eventKey;
      if (data.method === "stream" && data.event) {
        eventKey = data.event;
      } else {
        eventKey = data.method || data.event;
      }

      if (messageHandlers[eventKey]) {
        messageHandlers[eventKey](data);
      }
    } catch (error) {}
  }, []);

  const subscribe = useCallback((events) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      return;
    }

    const newEvents = events.filter((event) => !activeSubscriptions.has(event));

    if (newEvents.length > 0) {
      ws.current.send(
        JSON.stringify({
          method: "subscribe",
          events: newEvents,
        })
      );

      newEvents.forEach((event) => activeSubscriptions.add(event));
    }
  }, []);

  const unsubscribe = useCallback((events) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      events.forEach((event) => activeSubscriptions.delete(event));
      return;
    }

    const eventsToRemove = events.filter((event) =>
      activeSubscriptions.has(event)
    );

    if (eventsToRemove.length > 0) {
      ws.current.send(
        JSON.stringify({
          method: "unsubscribe",
          events: eventsToRemove,
        })
      );

      eventsToRemove.forEach((event) => activeSubscriptions.delete(event));
    }
  }, []);

  const logout = useCallback(() => {
    const userEvents = ["BL", "PO.ALL"];
    unsubscribe(userEvents);

    setIsLoggedIn(false);
    setUserBalance({});
    setUserOpenOrders([]);

    tokenRef.current = null;
    localStorage.removeItem("token");

    setTimeout(() => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        subscribe(["MK"]);
      }
    }, 100);
  }, [unsubscribe, subscribe]);

  const login = useCallback((token) => {
    tokenRef.current = token;
    localStorage.setItem("token", token);

    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(
        JSON.stringify({
          method: "login",
          token: `Bearer ${token}`,
        })
      );
    }
  }, []);

  useEffect(() => {
    const loginHandler = (data) => {
      if (data.status === "success") {
        setIsLoggedIn(true);

        setTimeout(() => {
          subscribe(["BL", "PO.ALL"]);
        }, 100);
      } else {
        setIsLoggedIn(false);
      }
    };

    registerHandler("login", loginHandler);

    return () => {
      unregisterHandler("login");
    };
  }, [registerHandler, unregisterHandler, subscribe]);

  const createWebSocket = useCallback(() => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (wsInstance) {
      wsInstance.close();
      wsInstance = null;
    }

    wsInstance = new WebSocket(process.env.REACT_APP_API_WS_URL);
    ws.current = wsInstance;

    const handleOpen = () => {
      setIsConnected(true);
      reconnectAttempts = 0; // Reset reconnect counter on successful connection

      subscribe(["MK"]);
      if (tokenRef.current) {
        ws.current.send(
          JSON.stringify({
            method: "login",
            token: `Bearer ${tokenRef.current}`,
          })
        );
      } else {
      }

      if (activeSubscriptions.size > 1) {
        const eventsToResubscribe = Array.from(activeSubscriptions).filter(
          (event) => event !== "MK" // Already subscribed above
        );

        if (eventsToResubscribe.length > 0) {
          ws.current.send(
            JSON.stringify({
              method: "subscribe",
              events: eventsToResubscribe,
            })
          );
        }
      }
    };

    const handleClose = (event) => {
      setIsConnected(false);
      setIsLoggedIn(false);
      wsInstance = null;

      activeSubscriptions.delete("BL");
      activeSubscriptions.delete("PO.ALL");

      if (event.code !== 1000 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts++;

        reconnectTimeout = setTimeout(() => {
          createWebSocket();
        }, RECONNECT_DELAY);
      } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      }
    };

    const handleError = (error) => {};

    ws.current.addEventListener("open", handleOpen);
    ws.current.addEventListener("close", handleClose);
    ws.current.addEventListener("error", handleError);
    ws.current.addEventListener("message", handleMessage);

    return () => {
      if (ws.current) {
        ws.current.removeEventListener("open", handleOpen);
        ws.current.removeEventListener("close", handleClose);
        ws.current.removeEventListener("error", handleError);
        ws.current.removeEventListener("message", handleMessage);
      }
    };
  }, [subscribe, handleMessage]);

  // Initialize WebSocket connection - runs once on mount
  useEffect(() => {
    const cleanup = createWebSocket();

    // Cleanup on unmount
    return () => {
      cleanup();
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (wsInstance) {
        wsInstance.close(1000, "Component unmounting"); // Normal closure
      }
    };
  }, [createWebSocket]);

  useEffect(() => {
    const checkTokenChange = setInterval(() => {
      const currentToken = localStorage.getItem("token");

      // Token was removed (logout)
      if (tokenRef.current && !currentToken) {
        logout();
      }
      // Token was added (login)
      else if (!tokenRef.current && currentToken) {
        login(currentToken);
      }
      // Token changed
      else if (tokenRef.current !== currentToken && currentToken) {
        login(currentToken);
      }
    }, 1000); // Check every second

    return () => clearInterval(checkTokenChange);
  }, [login, logout]);

  // Ping/Pong mechanism to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({ method: "ping" }));
      }
    }, 30000); // Ping every 30 seconds

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  // Register handler for Market Data (MK)
  useEffect(() => {
    const mkHandler = (data) => {
      const dataArray = data?.data;
      if (!dataArray) {
        return;
      }

      // Store all market data
      setMKData(dataArray);
      setCoinsData(dataArray);

      // Extract unique quote currencies (USDT, BTC, etc.)
      const uniqueQuotes = [...new Set(dataArray.map((item) => item.quote))];
      setPairs(uniqueQuotes);

      // Sort by volume and get top 3
      const sortedByVolume = [...dataArray].sort(
        (a, b) => b.base_volume - a.base_volume
      );
      const topVolume = sortedByVolume.slice(0, 3);
      setVolumeData(topVolume);

      // Get top 3 increasing prices
      const topIncreasing = dataArray
        .filter((item) => item.change_in_price > 0)
        .sort((a, b) => b.change_in_price - a.change_in_price)
        .slice(0, 3);
      setIncreasingData(
        topIncreasing.length > 0 ? topIncreasing : sortedByVolume.slice(0, 3)
      );

      // Get bottom 3 decreasing prices
      const sortedByChange = [...dataArray].sort(
        (a, b) => a.change_in_price - b.change_in_price
      );
      const topDecreasing = sortedByChange.slice(0, 3);
      setDecreasingData(topDecreasing);
    };

    registerHandler("MK", mkHandler);

    return () => {
      unregisterHandler("MK");
    };
  }, [registerHandler, unregisterHandler]);

  // Register handler for Balance (BL)
  useEffect(() => {
    const blHandler = (data) => {
      if (data?.data && typeof data.data === "object") {
        setUserBalance((prevBalance) => {
          return {
            ...prevBalance,
            ...data.data,
          };
        });
      }
    };

    registerHandler("BL", blHandler);

    return () => {
      unregisterHandler("BL");
    };
  }, [registerHandler, unregisterHandler]);

  // Register handler for User Open Orders (PO.ALL)
  useEffect(() => {
    const poHandler = (data) => {
      // Ensure data is an array
      let receivedOrders = Array.isArray(data.data) ? data.data : [data.data];

      setUserOpenOrders((prevOrders) => {
        let updatedOrders = [...prevOrders];

        receivedOrders.forEach((order) => {
          // Find if order already exists
          const existingIndex = updatedOrders.findIndex(
            (item) => item.order_id === order.order_id
          );

          if (existingIndex !== -1) {
            // Remove existing order (will be re-added if still active)
            updatedOrders.splice(existingIndex, 1);
          }

          // Add order to list
          updatedOrders.push(order);
        });

        // Filter to only show active orders (status === false means active)
        return updatedOrders.filter((order) => order?.status === false);
      });
    };

    registerHandler("PO.ALL", poHandler);

    return () => {
      unregisterHandler("PO.ALL");
    };
  }, [registerHandler, unregisterHandler]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      // Connection status
      isConnected,
      isLoggedIn,

      // Subscription methods
      subscribe,
      unsubscribe,
      registerHandler,
      unregisterHandler,

      // ✅ NEW: Auth methods
      login,
      logout,

      // Market data
      MKData,
      volumeData,
      increasingData,
      decreasingData,
      coinsData,
      pairs,

      // Trading data
      asks,
      bids,
      trades,
      latestTradePrice,
      latestTradeType,
      latestPrices,

      // User data
      userBalance,
      userOpenOrders,

      // State setters for trading page
      setAsks,
      setBids,
      setTrades,
      setLatestTradePrice,
      setLatestTradeType,
      setLatestPrices,
    }),
    [
      isConnected,
      isLoggedIn,
      subscribe,
      unsubscribe,
      registerHandler,
      unregisterHandler,
      login,
      logout,
      MKData,
      volumeData,
      increasingData,
      decreasingData,
      coinsData,
      pairs,
      asks,
      bids,
      trades,
      latestTradePrice,
      latestTradeType,
      latestPrices,
      userBalance,
      userOpenOrders,
    ]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook for easy context access
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
};
